import { SpreadsheetPreview } from '@monorepo/shared/componentsV2/SpreadsheetPreview/SpreadsheetPreview';
import { bodyRegular } from '@monorepo/shared/styles/text';
import React from 'react';
import styled from 'styled-components';

const StyledEmbed = styled.embed`
  width: 100%;
  height: 100%;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const StyledPreviewUnavailable = styled.div`
  ${bodyRegular}
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 1rem;
  color: ${({ theme }) => theme.colors.gray666};
  text-align: center;
`;

enum FilePreviewKind {
  IMAGE = 'image',
  PDF = 'pdf',
  SPREADSHEET = 'spreadsheet',
  UNAVAILABLE = 'unavailable',
}

const SPREADSHEET_MIME_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const SPREADSHEET_EXTENSIONS = ['.csv', '.xls', '.xlsx'];

function getFilePreviewKind(
  mimeType?: string,
  fileName?: string,
): FilePreviewKind {
  const mime = mimeType?.toLowerCase() ?? '';
  const lowerCaseName = fileName?.toLowerCase() ?? '';

  if (mime.startsWith('image/')) {
    return FilePreviewKind.IMAGE;
  }
  if (mime.includes('pdf') || lowerCaseName.endsWith('.pdf')) {
    return FilePreviewKind.PDF;
  }
  if (
    SPREADSHEET_MIME_TYPES.includes(mime) ||
    SPREADSHEET_EXTENSIONS.some((extension) =>
      lowerCaseName.endsWith(extension),
    )
  ) {
    return FilePreviewKind.SPREADSHEET;
  }
  return FilePreviewKind.UNAVAILABLE;
}

type FilePreviewProps = {
  src: string;
  mimeType?: string;
  fileName?: string;
  title?: string;
};

export function FilePreview({
  src,
  mimeType,
  fileName,
  title = 'Uploaded file preview',
}: FilePreviewProps) {
  switch (getFilePreviewKind(mimeType, fileName)) {
    case FilePreviewKind.IMAGE:
      return <StyledImage src={src} alt={title} />;
    case FilePreviewKind.PDF:
      return <StyledEmbed src={src} type="application/pdf" title={title} />;
    case FilePreviewKind.SPREADSHEET:
      return <SpreadsheetPreview key={src} src={src} title={title} />;
    case FilePreviewKind.UNAVAILABLE:
    default:
      return (
        <StyledPreviewUnavailable>
          Preview is not available for this file type.
        </StyledPreviewUnavailable>
      );
  }
}
